import {
	useDeleteManyProductMutation,
	useGetAllProductsQuery,
	useGetBrandQuery,
} from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import { Box, FileText, LayoutGrid, List, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AddToQuoteModal } from '../../components/AddToQuoteModal';
import { ProductCard } from '../../components/ProductCard';
import { BrandSelector } from '../../components/selectors/BrandSelector';
import { ProductActionMenu, type ProductAction } from '../../components/products/ProductActionMenu';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { SearchInput } from '../../components/ui/search-input';
import { SelectionBar } from '../../components/ui/selection-bar';
import { SortableTableHead } from '../../components/ui/sortable-table-head';
import { Tabs } from '../../components/ui/tabs';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam } from '../../hooks/useQueryParam';
import { cn, formatDate } from '../../lib/utils';
import { CommunityTab, productGridRows } from './CommunityTab';
import { CreateProductModal } from './CreateProductModal';
import { EditProductModal } from './EditProductModal';

/* ── Product thumbnail (list view) ── */

function ProductThumb({
	images,
	name,
}: {
	images?: Array<{ url?: string | null; isPrimary?: boolean | null }> | null;
	name: string;
}) {
	const primary = images?.find((i) => i.isPrimary) ?? images?.[0];
	if (primary?.url) {
		return (
			<img
				src={primary.url}
				alt={name}
				className="h-7 w-10 shrink-0 rounded-[var(--r-1)] border border-line-1 object-cover"
			/>
		);
	}
	return (
		<div className="flex h-7 w-10 shrink-0 items-center justify-center rounded-[var(--r-1)] border border-line-1 bg-surface-fill">
			<Box size={14} className="text-text-4" />
		</div>
	);
}

/* ── View toggle ── */

function ViewToggle({
	value,
	onChange,
}: {
	value: 'grid' | 'list';
	onChange: (v: 'grid' | 'list') => void;
}) {
	return (
		<div className="inline-flex h-7 overflow-hidden rounded-[var(--r-2)] border border-line-2 bg-surface-input">
			{[
				{ id: 'list' as const, icon: List, label: 'List view' },
				{ id: 'grid' as const, icon: LayoutGrid, label: 'Grid view' },
			].map((v, i) => {
				const active = value === v.id;
				return (
					<button
						key={v.id}
						onClick={() => onChange(v.id)}
						title={v.label}
						aria-label={v.label}
						className={cn(
							'flex h-full w-7 items-center justify-center transition-[background,color] duration-[120ms]',
							i > 0 && 'border-l border-line-2',
							active
								? 'bg-surface text-text-1'
								: 'bg-transparent text-text-3 hover:text-text-1'
						)}
					>
						<v.icon size={14} />
					</button>
				);
			})}
		</div>
	);
}

/* ── Empty state ── */

function EmptyState({ search }: { search: string }) {
	return (
		<Card className="border-line-1 bg-surface py-16">
			<div className="flex flex-col items-center gap-3 text-center">
				<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
					<Box size={20} />
				</div>
				<div className="text-base text-text-2">No products found</div>
				<div className="text-sm text-text-4">
					{search ? 'Try a different search term.' : 'No products have been added yet.'}
				</div>
			</div>
		</Card>
	);
}

/* ── Skeletons ── */

function GridSkeletons() {
	return (
		<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{Array.from({ length: 8 }).map((_, i) => (
				<Card key={i} className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="animate-pulse bg-surface-fill" style={{ aspectRatio: '1' }} />
					<div className="p-3.5">
						<div className="mb-2 h-4 w-3/4 animate-pulse rounded bg-surface-fill" />
						<div className="h-3 w-1/2 animate-pulse rounded bg-surface-fill" />
					</div>
				</Card>
			))}
		</div>
	);
}

function TableSkeleton() {
	return (
		<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
			{Array.from({ length: 6 }).map((_, i) => (
				<div
					key={i}
					className="flex items-center gap-4 px-4 py-3"
					style={{ borderBottom: i < 5 ? '0.5px solid var(--line-1)' : 'none' }}
				>
					<div className="h-7 w-10 animate-pulse rounded-[var(--r-1)] bg-surface-fill" />
					<div className="h-4 w-40 animate-pulse rounded bg-surface-fill" />
					<div className="ml-auto h-3 w-20 animate-pulse rounded bg-surface-fill" />
				</div>
			))}
		</Card>
	);
}

/**
 * One row of products as laid out at the current viewport, for sizing scroll-mode batches.
 * The grid is the shared product-card grid (see `productGridRows`); a table row is the 28px
 * thumbnail / action button plus TableCell's 10px vertical padding and the 1px border.
 */
function productRows(view: string): RowEstimate {
	return view === 'grid' ? productGridRows() : { rowHeight: 49 };
}

/* ════════════════════════ PRODUCTS PAGE ════════════════════════ */

export function ProductsPage() {
	const navigate = useNavigate();
	// 'mine' = the organization's own products, 'community' = what our team has published.
	const [tab, setTab] = useQueryParam('tab', 'mine');
	const isCommunity = tab === 'community';
	const [, setTagFilter] = useQueryParam('tags', '');
	// In the URL, like the admin page's brand filter, so a filtered view survives navigation.
	const [brandFilter, setBrandFilter] = useQueryParam('brand', '');
	// Arriving with a brand in the URL, the selector has not seen the pick; look its name up.
	const { data: brandData } = useGetBrandQuery({
		variables: { id: brandFilter },
		fetchPolicy: 'no-cache',
		skip: !brandFilter,
	});
	const [createOpen, setCreateOpen] = useState(false);
	const [editProductId, setEditProductId] = useState<string | null>(null);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	// The action being confirmed and the products it applies to: one product from its menu, or the
	// selection from the selection bar — single and bulk run through the same dialogs.
	const [pending, setPending] = useState<{
		kind: 'quote' | 'delete';
		ids: string[];
		fromSelection: boolean;
	} | null>(null);
	const {
		search,
		setSearch,
		debouncedSearch,
		page,
		setPage,
		view,
		setView,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		mode,
		setMode,
		request,
		loadMore,
		reload,
		limit,
	} = useListParams({
		defaultSort: 'name',
		defaultDir: 'asc',
		defaultView: 'grid',
		scrollRows: productRows,
	});

	const { data, loading, refetch } = useGetAllProductsQuery({
		variables: {
			input: {
				pagination: request,
				order: { field: sortField, direction: sortDirection },
				search: debouncedSearch || undefined,
				brandId: brandFilter || undefined,
			},
		},
		fetchPolicy: 'no-cache',
		skip: isCommunity,
	});

	const list = usePagedList(
		{ mode, request, limit, setPage, loadMore },
		data?.getAllProduct,
		loading
	);
	const products = list.items;
	const total = list.pageInfo?.totalCount ?? 0;

	const [deleteManyProduct, { loading: deleteLoading }] = useDeleteManyProductMutation();

	const toggleSelect = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};
	const clearSelection = () => setSelected(new Set());

	// Bound the selection to what is on screen: bulk actions only see the current page's products,
	// so a selection carried across pages, searches or filters would be silently cut short.
	// Scrolling keeps earlier rows on screen, so in scroll mode loading more does not clear it.
	const shownPage = mode === 'pages' ? page : 0;
	useEffect(() => {
		setSelected(new Set());
	}, [shownPage, mode, debouncedSearch, brandFilter]);

	const productNames: Record<string, string> = Object.fromEntries(
		products.map((p) => [p.id, p.name])
	);
	const pendingIds = pending?.ids ?? [];
	const pendingCount = pendingIds.length;
	const pendingSubject =
		pendingCount === 1
			? `"${productNames[pendingIds[0]] ?? 'this product'}"`
			: `${pendingCount} products`;

	// Clear the selection only when the action came from it.
	const finishAction = () => {
		if (pending?.fromSelection) clearSelection();
		setPending(null);
		reload(refetch);
	};

	const handleDelete = async () => {
		try {
			const { data } = await deleteManyProduct({ variables: { input: { ids: pendingIds } } });
			// All-or-nothing on the backend: success false means nothing was deleted.
			if (!data?.deleteManyProduct?.success) {
				toast.error(data?.deleteManyProduct?.message ?? 'Failed to delete');
				return;
			}
			toast.success(`${pendingSubject} deleted`);
			finishAction();
		} catch {
			toast.error('Failed to delete');
		}
	};

	/** Every product action, in one order, for one product (card/row menu) or the selection (bar). */
	const actionsFor = (rows: typeof products, fromSelection: boolean): ProductAction[] => {
		const ids = rows.map((r) => r.id);
		const open = (kind: 'quote' | 'delete') => () => setPending({ kind, ids, fromSelection });
		const single = !fromSelection && rows.length === 1 ? rows[0] : null;
		return [
			...(single
				? [
						{
							id: 'edit',
							label: 'Edit',
							icon: Pencil,
							onClick: () => setEditProductId(single.id),
						},
					]
				: []),
			{ id: 'quote', label: 'Add to quote', icon: FileText, onClick: open('quote') },
			{
				id: 'delete',
				label: 'Delete',
				icon: Trash2,
				destructive: true,
				onClick: open('delete'),
			},
		];
	};

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Header ── */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
						Products
					</h1>
					<p className="text-base text-text-3">
						{isCommunity
							? 'Products picked by our team. Add any of them to your products to make them your own.'
							: `Browse your product catalog — ${total} products.`}
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
						<Plus size={14} data-icon="inline-start" />
						Add product
					</Button>
				</div>
			</div>

			{/* ── Tabs + filters row (same layout as the admin products page) ── */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						// Search, page and selection belong to one tab; start the other clean.
						setTab(v);
						setSearch('');
						setPage(1);
						setTagFilter('');
						setBrandFilter('');
						clearSelection();
					}}
					items={[
						{ value: 'mine', label: 'My products' },
						{ value: 'community', label: 'Community' },
					]}
				/>
				<div className="flex items-center gap-2">
					<SearchInput
						value={search}
						onChange={(v) => {
							setSearch(v);
							setPage(1);
						}}
						placeholder={isCommunity ? 'Search community…' : 'Search products…'}
						className="flex-1 lg:flex-none lg:w-[240px]"
					/>
					{!isCommunity && (
						<BrandSelector
							value={brandFilter}
							selectedLabel={brandData?.getBrand?.name ?? undefined}
							onChange={(v) => {
								setBrandFilter(v);
								setPage(1);
								clearSelection();
							}}
							clearable
							placeholder="All brands"
							className="w-[180px] shrink-0"
						/>
					)}
					{!isCommunity && (
						<ViewToggle value={view as 'grid' | 'list'} onChange={setView} />
					)}
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{isCommunity ? (
				<CommunityTab />
			) : (
				<>
					{/* ── Content ── */}
					{list.loading ? (
						view === 'grid' ? (
							<GridSkeletons />
						) : (
							<TableSkeleton />
						)
					) : products.length === 0 ? (
						<EmptyState search={search} />
					) : view === 'grid' ? (
						<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
							{products.map((p) => (
								<ProductCard
									key={p.id}
									name={p.name}
									sku={p.sku}
									images={p.images}
									tags={p.tags}
									isSelected={selected.has(p.id)}
									onSelect={() => toggleSelect(p.id)}
									onClick={() =>
										selected.size > 0
											? toggleSelect(p.id)
											: navigate(`/products/${p.id}`)
									}
									actions={actionsFor([p], false)}
								/>
							))}
						</div>
					) : (
						<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
							<div className="overflow-x-auto">
								<Table className="min-w-[700px]">
									<TableHeader>
										<TableRow className="border-line-1 hover:bg-transparent">
											<TableHead className="w-10">
												<Checkbox
													checked={
														selected.size === products.length &&
														products.length > 0
													}
													indeterminate={
														selected.size > 0 &&
														selected.size < products.length
													}
													onCheckedChange={() => {
														if (selected.size === products.length) {
															clearSelection();
														} else {
															setSelected(
																new Set(products.map((p) => p.id))
															);
														}
													}}
												/>
											</TableHead>
											<SortableTableHead
												sortKey="name"
												activeKey={sortField}
												direction={sortDir}
												onSort={toggleSort}
												className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
											>
												Product
											</SortableTableHead>
											<SortableTableHead
												sortKey="sku"
												activeKey={sortField}
												direction={sortDir}
												onSort={toggleSort}
												className="w-[130px] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
											>
												SKU
											</SortableTableHead>
											<SortableTableHead
												sortKey="createdAt"
												activeKey={sortField}
												direction={sortDir}
												onSort={toggleSort}
												className="w-[110px] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
											>
												Created
											</SortableTableHead>
											<TableHead className="w-10" />
										</TableRow>
									</TableHeader>
									<TableBody>
										{products.map((p) => (
											<TableRow
												key={p.id}
												className={cn(
													'cursor-pointer border-line-1 transition-colors duration-[180ms] last:border-b-0',
													selected.has(p.id)
														? 'bg-accent-soft hover:bg-accent-soft'
														: 'hover:bg-surface-hover'
												)}
												onClick={() =>
													selected.size > 0
														? toggleSelect(p.id)
														: navigate(`/products/${p.id}`)
												}
											>
												<TableCell>
													<div onClick={(e) => e.stopPropagation()}>
														<Checkbox
															checked={selected.has(p.id)}
															onCheckedChange={() =>
																toggleSelect(p.id)
															}
														/>
													</div>
												</TableCell>
												<TableCell>
													<div className="flex items-center gap-3">
														<ProductThumb
															images={p.images}
															name={p.name}
														/>
														<span className="text-base font-medium text-text-1">
															{p.name}
														</span>
													</div>
												</TableCell>
												<TableCell className="font-mono text-base text-text-2">
													{p.sku || '—'}
												</TableCell>
												<TableCell className="text-base text-text-3">
													{p.createdAt ? formatDate(p.createdAt) : '—'}
												</TableCell>
												<TableCell>
													<div className="flex justify-end">
														<ProductActionMenu
															actions={actionsFor([p], false)}
														/>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						</Card>
					)}

					{/* ── Selection bar ── */}
					<SelectionBar
						count={selected.size}
						actions={actionsFor(
							products.filter((p) => selected.has(p.id)),
							true
						)}
						onClear={clearSelection}
					/>

					<ListFooter list={list} />
				</>
			)}

			{/* ── Modals ── */}
			<CreateProductModal
				open={createOpen}
				onOpenChange={setCreateOpen}
				onSuccess={() => reload(refetch)}
			/>

			{editProductId && (
				<EditProductModal
					open={!!editProductId}
					onOpenChange={(v) => !v && setEditProductId(null)}
					productId={editProductId}
					onSaved={() => reload(refetch)}
				/>
			)}

			<AddToQuoteModal
				open={pending?.kind === 'quote'}
				onOpenChange={(v) => !v && setPending(null)}
				productIds={pendingIds}
				productNames={productNames}
				onSuccess={finishAction}
			/>

			<ConfirmDialog
				open={pending?.kind === 'delete'}
				onOpenChange={(v) => !v && setPending(null)}
				title={pendingCount > 1 ? 'Delete products' : 'Delete product'}
				description={`Are you sure you want to delete ${pendingSubject}? This action cannot be undone.`}
				confirmLabel={pendingCount > 1 ? 'Delete all' : 'Delete'}
				loading={deleteLoading}
				onConfirm={handleDelete}
			/>
		</div>
	);
}
